/**
 * Shown on Settings → Workspace when the tenant is on a plan with a non-zero
 * `extra_team_member_price`. Lets owners purchase one additional team-member
 * slot — a $10/mo add-on on Pro — up to the plan's hard cap.
 *
 * On click: hits /api/billing/team-members/add which returns a Stripe
 * Checkout URL; we redirect the browser there. On success Stripe bounces
 * back to /app/settings?session_id=... and the webhook (plus the status
 * poll in /billing/status) bumps the tenant's seats_limit by 1.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Loader2 } from "lucide-react";

export default function TeamMemberSlotsCard() {
  const { tenant } = useAuth();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  // Pull the live price catalog so a pricing change on the backend doesn't
  // need a frontend redeploy. Falls back silently if the endpoint is down.
  useEffect(() => {
    if (!tenant?.plan) return;
    let cancelled = false;
    api.get("/billing/plans")
      .then((r) => {
        if (cancelled) return;
        const p = (r.data?.plans || []).find((x) => x.id === tenant.plan);
        if (p) setPlan(p);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenant?.plan]);

  // Watch for the Stripe success callback and surface a toast once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id")) {
      toast.success("Payment received — your team-member slot will activate within a few seconds.");
      params.delete("session_id");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
    } else if (params.get("canceled")) {
      toast.message("Checkout canceled — no changes were made.");
      params.delete("canceled");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
    }
  }, []);

  if (!plan || (plan.extra_team_member_price || 0) <= 0) return null;

  const current = Number(tenant?.seats_limit) || 0;
  const headroom = Math.max(0, plan.max_team_members - current);
  const price = plan.extra_team_member_price;

  const purchase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/team-members/add", { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="ll-card-flat p-5 mb-6"
      style={{ background: "#F0FDF4", borderColor: "#86EFAC" }}
      data-testid="team-member-slots-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <UsersIcon size={11} /> Team-member slots
          </div>
          <div className="font-display font-bold text-xl mt-1">
            {current} <span className="text-[var(--ll-ink-soft)] text-base font-normal">of {plan.max_team_members} on {plan.name}</span>
          </div>
          <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            {headroom > 0
              ? <>Add one more team-member slot for <strong>${price}/mo</strong>. {headroom} slot{headroom === 1 ? "" : "s"} still available before you hit the {plan.name} cap.</>
              : <>You&apos;ve reached the {plan.name} plan&apos;s team-member cap. Upgrade to Business for larger teams.</>}
          </p>
        </div>
        <button
          type="button"
          onClick={purchase}
          disabled={busy || headroom <= 0}
          data-testid="team-member-slots-buy"
          className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60 shrink-0"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          {busy ? "Opening checkout…" : `Add one · $${price}/mo`}
        </button>
      </div>
    </div>
  );
}
